// 渲染优化服务 - 优化渲染性能，减少重绘重排
class RenderOptimizer {
  constructor() {
    this.optimizations = {
      virtualScrolling: false,
      lazyLoading: false,
      componentCaching: false,
      batchUpdates: false,
      debounceResize: false,
      throttleScroll: false
    }
    
    this.cache = new Map()
    this.observers = new Map()
    this.pendingUpdates = new Set()
    this.isOptimizing = false
    
    this.thresholds = {
      frameRate: 30, // 最低帧率
      updateDelay: 16, // 更新延迟 16ms (60fps)
      resizeDebounce: 100, // 窗口大小调整防抖
      scrollThrottle: 16 // 滚动节流
    }
    
    this.init()
  }
  
  // 初始化渲染优化
  init() {
    if (this.isOptimizing) return
    
    this.isOptimizing = true
    this.setupOptimizations()
    this.setupPerformanceObservers()
    
    // console.log('✅ 渲染优化系统已启动')
  }
  
  // 设置优化选项
  setupOptimizations() {
    // 启用所有优化
    Object.keys(this.optimizations).forEach(key => {
      this.optimizations[key] = true
    })
    
    // 设置虚拟滚动
    if (this.optimizations.virtualScrolling) {
      this.setupVirtualScrolling()
    }
    
    // 设置懒加载
    if (this.optimizations.lazyLoading) {
      this.setupLazyLoading()
    }
    
    // 设置组件缓存
    if (this.optimizations.componentCaching) {
      this.setupComponentCaching()
    }
    
    // 设置批量更新
    if (this.optimizations.batchUpdates) {
      this.setupBatchUpdates()
    }
    
    // 设置防抖和节流
    this.setupDebounceAndThrottle()
  }
  
  // 设置虚拟滚动
  setupVirtualScrolling() {
    // 虚拟滚动实现
    this.virtualScrollConfig = {
      itemHeight: 50,
      containerHeight: 400,
      buffer: 5,
      threshold: 0.1
    }
  }
  
  // 设置懒加载
  setupLazyLoading() {
    // 图片懒加载
    this.setupImageLazyLoading()
    
    // 组件懒加载
    this.setupComponentLazyLoading()
  }
  
  // 设置图片懒加载
  setupImageLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target
            if (img.dataset.src) {
              img.src = img.dataset.src
              img.removeAttribute('data-src')
              imageObserver.unobserve(img)
            }
          }
        })
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      })
      
      this.observers.set('imageLazy', imageObserver)
      
      // 观察所有懒加载图片
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img)
      })
    }
  }
  
  // 设置组件懒加载
  setupComponentLazyLoading() {
    if ('IntersectionObserver' in window) {
      const componentObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const component = entry.target
            if (component.dataset.component) {
              this.loadComponent(component.dataset.component, component)
              componentObserver.unobserve(component)
            }
          }
        })
      }, {
        rootMargin: '100px 0px',
        threshold: 0.01
      })
      
      this.observers.set('componentLazy', componentObserver)
      
      // 观察所有懒加载组件
      document.querySelectorAll('[data-component]').forEach(component => {
        componentObserver.observe(component)
      })
    }
  }
  
  // 加载组件
  async loadComponent(componentName, container) {
    try {
      const component = await import(`../components/${componentName}.jsx`)
      if (component.default) {
        // 渲染组件到容器
        this.renderComponent(component.default, container)
      }
    } catch (error) {
      console.error(`加载组件失败: ${componentName}`, error)
    }
  }
  
  // 渲染组件
  renderComponent(Component, container) {
    // 这里可以实现组件的渲染逻辑
    // 例如使用 React 的 createRoot 或 Vue 的 createApp
    console.log('渲染组件到容器:', container)
  }
  
  // 设置组件缓存
  setupComponentCaching() {
    // 组件缓存配置
    this.cacheConfig = {
      maxSize: 100,
      ttl: 300000, // 5分钟
      strategy: 'lru' // 最近最少使用
    }
  }
  
  // 设置批量更新
  setupBatchUpdates() {
    // 使用 requestAnimationFrame 进行批量更新
    this.batchUpdate = this.batchUpdate.bind(this)
  }
  
  // 批量更新
  batchUpdate(updateFn) {
    if (typeof updateFn !== 'function') return
    
    this.pendingUpdates.add(updateFn)
    
    if (this.pendingUpdates.size === 1) {
      requestAnimationFrame(() => {
        this.flushUpdates()
      })
    }
  }
  
  // 刷新更新
  flushUpdates() {
    const updates = Array.from(this.pendingUpdates)
    this.pendingUpdates.clear()
    
    updates.forEach(update => {
      try {
        update()
      } catch (error) {
        console.error('批量更新失败:', error)
      }
    })
  }
  
  // 设置防抖和节流
  setupDebounceAndThrottle() {
    // 窗口大小调整防抖
    if (this.optimizations.debounceResize) {
      this.debouncedResize = this.debounce(this.handleResize.bind(this), this.thresholds.resizeDebounce)
      window.addEventListener('resize', this.debouncedResize)
    }
    
    // 滚动节流
    if (this.optimizations.throttleScroll) {
      this.throttledScroll = this.throttle(this.handleScroll.bind(this), this.thresholds.scrollThrottle)
      window.addEventListener('scroll', this.throttledScroll)
    }
  }
  
  // 处理窗口大小调整
  handleResize() {
    console.log('窗口大小调整')
    // 重新计算布局
    this.recalculateLayout()
  }
  
  // 处理滚动
  handleScroll() {
    // 滚动优化
    this.optimizeScroll()
  }
  
  // 重新计算布局
  recalculateLayout() {
    // 重新计算虚拟滚动
    if (this.optimizations.virtualScrolling) {
      this.updateVirtualScroll()
    }
    
    // 重新计算懒加载
    if (this.optimizations.lazyLoading) {
      this.updateLazyLoading()
    }
  }
  
  // 更新虚拟滚动
  updateVirtualScroll() {
    // 虚拟滚动更新逻辑
    console.log('更新虚拟滚动')
  }
  
  // 更新懒加载
  updateLazyLoading() {
    // 懒加载更新逻辑
    console.log('更新懒加载')
  }
  
  // 优化滚动
  optimizeScroll() {
    // 滚动优化逻辑
    // 例如：暂停非关键动画，减少重绘
  }
  
  // 设置性能观察器
  setupPerformanceObservers() {
    if ('PerformanceObserver' in window) {
      // 监控长任务
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          entries.forEach(entry => {
            this.handleLongTask(entry)
          })
        })
        longTaskObserver.observe({ entryTypes: ['longtask'] })
        this.observers.set('longTask', longTaskObserver)
      } catch (error) {
        console.warn('长任务监控不可用:', error)
      }
      
      // 监控布局偏移
      try {
        const layoutShiftObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          entries.forEach(entry => {
            this.handleLayoutShift(entry)
          })
        })
        layoutShiftObserver.observe({ entryTypes: ['layout-shift'] })
        this.observers.set('layoutShift', layoutShiftObserver)
      } catch (error) {
        // console.warn('布局偏移监控不可用:', error)
      }
    }
  }
  
  // 处理长任务
  handleLongTask(entry) {
    // console.warn('检测到长任务:', entry.duration)
    
    // 如果任务时间过长，建议优化
    if (entry.duration > 50) {
      this.suggestOptimization('longTask', entry.duration)
    }
  }
  
  // 处理布局偏移
  handleLayoutShift(entry) {
    if (entry.hadRecentInput) return
    
    // console.warn('检测到布局偏移:', entry.value)
    
    // 如果偏移过大，建议优化
    if (entry.value > 0.1) {
      this.suggestOptimization('layoutShift', entry.value)
    }
  }
  
  // 建议优化
  suggestOptimization(type, value) {
    // 静默记录，不在控制台显示
    const suggestions = {
      longTask: '考虑将长任务分解为多个小任务，或使用 Web Workers',
      layoutShift: '为动态内容预留空间，避免布局偏移'
    }
    
    // console.log(`优化建议 (${type}):`, suggestions[type], '值:', value)
  }
  
  // 防抖函数
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
  
  // 节流函数
  throttle(func, limit) {
    let inThrottle
    return function executedFunction(...args) {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  }
  
  // 缓存组件
  cacheComponent(key, component) {
    if (this.cache.size >= this.cacheConfig.maxSize) {
      // 使用 LRU 策略清理缓存
      this.clearOldestCache()
    }
    
    this.cache.set(key, {
      component,
      timestamp: Date.now(),
      accessCount: 0
    })
  }
  
  // 获取缓存组件
  getCachedComponent(key) {
    const cached = this.cache.get(key)
    if (cached) {
      cached.accessCount++
      cached.lastAccess = Date.now()
      return cached.component
    }
    return null
  }
  
  // 清理最旧的缓存
  clearOldestCache() {
    let oldestKey = null
    let oldestTime = Date.now()
    
    for (const [key, value] of this.cache.entries()) {
      if (value.timestamp < oldestTime) {
        oldestTime = value.timestamp
        oldestKey = key
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey)
    }
  }
  
  // 清理过期缓存
  clearExpiredCache() {
    const now = Date.now()
    const expiredKeys = []
    
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.cacheConfig.ttl) {
        expiredKeys.push(key)
      }
    }
    
    expiredKeys.forEach(key => {
      this.cache.delete(key)
    })
  }
  
  // 优化 DOM 操作
  optimizeDOMOperation(operation) {
    // 使用 DocumentFragment 减少重排
    const fragment = document.createDocumentFragment()
    
    // 执行操作
    const result = operation(fragment)
    
    // 一次性插入
    if (result && result.parentNode) {
      result.parentNode.appendChild(fragment)
    }
    
    return result
  }
  
  // 优化样式更新
  optimizeStyleUpdate(element, styles) {
    // 批量更新样式
    const style = element.style
    const keys = Object.keys(styles)
    
    // 使用 requestAnimationFrame 确保在下一帧更新
    requestAnimationFrame(() => {
      keys.forEach(key => {
        style[key] = styles[key]
      })
    })
  }
  
  // 优化动画
  optimizeAnimation(element, animation) {
    // 使用 transform 和 opacity 进行动画
    // 这些属性不会触发重排
    const optimizedAnimation = {
      transform: animation.transform || 'translate3d(0, 0, 0)',
      opacity: animation.opacity || 1,
      willChange: 'transform, opacity'
    }
    
    // 应用优化后的动画
    this.optimizeStyleUpdate(element, optimizedAnimation)
  }
  
  // 获取渲染性能统计
  getRenderStats() {
    return {
      cacheSize: this.cache.size,
      pendingUpdates: this.pendingUpdates.size,
      observers: this.observers.size,
      optimizations: { ...this.optimizations }
    }
  }
  
  // 启用优化
  enableOptimization(name) {
    if (name in this.optimizations) {
      this.optimizations[name] = true
      // console.log(`✅ 已启用优化: ${name}`)
    }
  }
  
  // 禁用优化
  disableOptimization(name) {
    if (name in this.optimizations) {
      this.optimizations[name] = false
      // console.log(`❌ 已禁用优化: ${name}`)
    }
  }
  
  // 清理资源
  cleanup() {
    // 清理观察器
    this.observers.forEach(observer => observer.disconnect())
    this.observers.clear()
    
    // 清理缓存
    this.cache.clear()
    
    // 清理待处理更新
    this.pendingUpdates.clear()
    
    // 移除事件监听器
    if (this.debouncedResize) {
      window.removeEventListener('resize', this.debouncedResize)
    }
    
    if (this.throttledScroll) {
      window.removeEventListener('scroll', this.throttledScroll)
    }
    
    this.isOptimizing = false
    // console.log('🧹 渲染优化系统已清理')
  }
  
  // 停止优化
  stop() {
    this.cleanup()
    // console.log('⏹️ 渲染优化系统已停止')
  }
  
  // 销毁优化器
  destroy() {
    this.stop()
  }
}

// 创建单例实例
const renderOptimizer = new RenderOptimizer()

export default renderOptimizer

